import { useDriverFaceCluster } from '@cobliteam/hooks';
import Button from '@cobliteam/ui/components/Button';
import Divider from '@cobliteam/ui/components/Divider';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import type React from 'react';
import DriverFaceIdHeader from '../DriverFaceIdHeader';
import MainFaceImage from './components/MainFaceImage';
import SmallFaceImage from './components/SmallFaceImage';

type SmallFaceImageProps = React.ComponentProps<typeof SmallFaceImage>;

interface DriverFaceIdImagesSummaryProps {
  driverId: string;
  hasEditPermission: boolean;
  /** Images on draft */
  preview?: string[];
  onButtonClick: () => void;
}

const createSmallFacesProps = (imagesSrc: string[] = []): SmallFaceImageProps[] => {
  const topImages = imagesSrc.slice(0, 4);

  if (topImages.length === 0)
    return Array(3).fill({
      variant: 'placeholder',
    });

  const values: SmallFaceImageProps[] = topImages
    .map(
      (src): SmallFaceImageProps => ({
        variant: 'image',
        src,
      })
    )
    .concat([
      {
        variant: 'default',
      },
    ]);

  return values;
};

const DriverFaceIdImagesSummary = ({ driverId, preview, onButtonClick, hasEditPermission }: DriverFaceIdImagesSummaryProps) => {
  const { getDriverFaceCluster } = useDriverFaceCluster();
  const { data: faceId, isLoading } = getDriverFaceCluster(driverId, { enabled: true });

  const profileUrl = faceId?.profileImageUrl.imageUrl || '';
  const samplesUrl = faceId?.sampleImageUrls.map((it) => it.imageUrl).filter((it) => it !== profileUrl) || [];

  const images = preview ? preview : [profileUrl, ...samplesUrl];
  const [imageMain, ...imagesSamples] = images;

  const smallFaces = createSmallFacesProps(imagesSamples);

  const actionLabel = imageMain ? 'Gerenciar fotos' : 'Adicionar fotos';
  const permissionInfo = `Você não tem permissão para ${actionLabel.toLowerCase()}. Peça acesso ao administrador.`;

  return (
    <div className='block'>
      <DriverFaceIdHeader />
      <div className='mt-3 flex w-full rounded-xl border border-gray-6 px-5 py-3'>
        <div className='flex w-full items-center gap-6'>
          <MainFaceImage src={imageMain} isLoading={isLoading} />
          <Divider className='h-3/4' orientation='vertical' />
          <div className='flex'>
            {smallFaces.map((props, i) => (
              <SmallFaceImage key={i} {...props} />
            ))}
          </div>
          <div className='flex-1' />
          <Tooltip enable={!hasEditPermission} content={permissionInfo}>
            <Button disabled={!hasEditPermission} variant='outlined' startIcon={<Icon name='images' />} onClick={onButtonClick}>
              {actionLabel}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default DriverFaceIdImagesSummary;
